import React, {useState, useCallback, useEffect} from "react";
import useApiStable from "../../utils/useApiStable";
import MatchLineupEditor from "./MatchLineupEditor";

const formatDate = (isoString) => {
    if (!isoString) return "—";
    const d = new Date(isoString);
    return d.toLocaleDateString("de-DE", {
        weekday: "short",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const AvailabilityButton = ({matchId, teamId, currentStatus, currentComment, isFuture, isMember, onChange}) => {
    const api = useApiStable();
    const [loading, setLoading] = useState(false);
    const [comment, setComment] = useState(currentComment || "");

    useEffect(() => {
        setComment(currentComment || "");
    }, [currentComment]);

    if (!isMember || !isFuture) return null;

    const save = async (status) => {
        setLoading(true);
        try {
            await api.post(`/league/teams/${teamId}/matches/${matchId}/availability`, {status, comment});
            onChange();
        } catch (err) {
            console.error("Verfügbarkeit konnte nicht gesetzt werden:", err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div onClick={e => e.stopPropagation()}>
            <div style={{display: "flex", gap: 8, marginBottom: 8}}>
                <button
                    onClick={() => save("AVAILABLE")}
                    disabled={loading}
                    style={{
                        flex: "1 1 0",
                        maxWidth: 160,
                        minHeight: 40,
                        padding: "8px 16px",
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: "pointer",
                        backgroundColor: currentStatus === "AVAILABLE" ? "#4CAF50" : "#fff",
                        color: currentStatus === "AVAILABLE" ? "#fff" : "#4CAF50",
                        border: "1px solid #4CAF50",
                        borderRadius: 6,
                    }}
                >
                    Zusagen
                </button>
                <button
                    onClick={() => save("UNAVAILABLE")}
                    disabled={loading}
                    style={{
                        flex: "1 1 0",
                        maxWidth: 160,
                        minHeight: 40,
                        padding: "8px 16px",
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: "pointer",
                        backgroundColor: currentStatus === "UNAVAILABLE" ? "#e65100" : "#fff",
                        color: currentStatus === "UNAVAILABLE" ? "#fff" : "#e65100",
                        border: "1px solid #e65100",
                        borderRadius: 6,
                    }}
                >
                    Absagen
                </button>
            </div>
            <input
                type="text"
                value={comment}
                onChange={e => setComment(e.target.value)}
                onBlur={() => { if (currentStatus) save(currentStatus); }}
                onKeyDown={e => { if (e.key === "Enter" && currentStatus) save(currentStatus); }}
                placeholder="Kommentar (optional)..."
                maxLength={500}
                style={{
                    width: "100%",
                    padding: "8px 10px",
                    fontSize: 14,
                    border: "1px solid #ddd",
                    borderRadius: 6,
                    boxSizing: "border-box",
                    color: "#555",
                }}
            />
        </div>
    );
};

const AvailabilitySummary = ({availabilities, isMember}) => {
    if (!isMember || !availabilities || availabilities.length === 0) return null;

    const available = availabilities.filter(a => a.status === "AVAILABLE").length;
    const unavailable = availabilities.filter(a => a.status === "UNAVAILABLE").length;
    const noResponse = availabilities.filter(a => !a.status).length;

    return (
        <div style={{display: "flex", gap: 10, marginTop: 5, fontSize: 12}}>
            {available > 0 && <span style={{color: "#4CAF50", fontWeight: 600}}>✓ {available}</span>}
            {unavailable > 0 && <span style={{color: "#e65100", fontWeight: 600}}>✗ {unavailable}</span>}
            {noResponse > 0 && <span style={{color: "#bbb"}}>? {noResponse}</span>}
        </div>
    );
};

const PlayerAvailabilityList = ({availabilities, isMember}) => {
    if (!isMember || !availabilities || availabilities.length === 0) return null;

    const available = availabilities.filter(a => a.status === "AVAILABLE");
    const unavailable = availabilities.filter(a => a.status === "UNAVAILABLE");
    const noResponse = availabilities.filter(a => !a.status);

    const renderPlayer = (a, color) => (
        <div key={a.personId} style={{paddingLeft: 12}}>
            <span style={{color: "#aaa", marginRight: 4}}>•</span>
            <span style={{color}}>{a.personName}</span>
            {a.comment && (
                <span style={{color: "#888", fontStyle: "italic"}}> – {a.comment}</span>
            )}
        </div>
    );

    return (
        <div style={{marginTop: 8, fontSize: 12}}>
            {available.length > 0 && (
                <div style={{marginBottom: 4}}>
                    <div style={{color: "#4CAF50", fontWeight: 600}}>✓ Zugesagt</div>
                    {available.map(a => renderPlayer(a, "#333"))}
                </div>
            )}
            {unavailable.length > 0 && (
                <div style={{marginBottom: 4}}>
                    <div style={{color: "#e65100", fontWeight: 600}}>✗ Abgesagt</div>
                    {unavailable.map(a => renderPlayer(a, "#333"))}
                </div>
            )}
            {noResponse.length > 0 && (
                <div>
                    <div style={{color: "#999", fontWeight: 600}}>? Noch keine Antwort</div>
                    {noResponse.map(a => (
                        <div key={a.personId} style={{paddingLeft: 12}}>
                            <span style={{color: "#ccc", marginRight: 4}}>•</span>
                            <span style={{color: "#aaa"}}>{a.personName}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const ShareButton = ({ match }) => {
    const [copied, setCopied] = useState(false);

    const handleShare = async (e) => {
        e.stopPropagation();
        const date = formatDate(match.matchDateTime);
        const title = `${match.homeTeam} vs. ${match.guestTeam}`;
        const lines = [
            title,
            date,
            match.venue,
            match.league + (match.division ? ` – ${match.division}` : ""),
        ].filter(Boolean);
        const text = lines.join("\n");

        if (navigator.share) {
            try {
                await navigator.share({ title, text, url: window.location.href });
            } catch (err) {
                if (err.name !== "AbortError") console.error(err);
            }
        } else {
            try {
                await navigator.clipboard.writeText(text);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            } catch (err) {
                console.error("Kopieren fehlgeschlagen", err);
            }
        }
    };

    return (
        <button
            onClick={handleShare}
            title="Spiel teilen"
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                padding: "3px 10px",
                background: copied ? "#e8f5e9" : "#f5f5f5",
                color: copied ? "#2e7d32" : "#555",
                border: "1px solid #ddd",
                borderRadius: 4,
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
            }}
        >
            {copied ? "✓ Kopiert" : "⬆ Teilen"}
        </button>
    );
};

const MatchCard = ({match, teamId, isMember, isCaptain, canSeeLineup, currentUser, onAvailabilityChange, highlightMatchId}) => {
    const isPast = !match.future;
    const canExpand = isPast ? isMember : (canSeeLineup || isMember);
    const isHighlighted = highlightMatchId === match.matchId;
    const [expanded, setExpanded] = useState(isHighlighted && canExpand);

    let accentColor = "#e0e0e0";
    let cardBg = "#fff";
    if (!isPast && isMember) {
        if (match.myAvailability === "AVAILABLE") {
            accentColor = "#4CAF50";
            cardBg = "#f9fff9";
        } else if (match.myAvailability === "UNAVAILABLE") {
            accentColor = "#ef9a9a";
            cardBg = "#fff8f8";
        } else {
            accentColor = "#b0bec5";
            cardBg = "#f8f9fa";
        }
    } else if (!isPast) {
        accentColor = "#1976d2";
    }

    return (
        <div
            id={`match-${match.matchId}`}
            style={{
                background: cardBg,
                border: "1px solid #e8e8e8",
                borderLeft: `3px solid ${accentColor}`,
                borderRadius: "0 6px 6px 0",
                marginBottom: 8,
            }}
        >
            <button
                type="button"
                onClick={canExpand ? () => setExpanded(v => !v) : undefined}
                disabled={!canExpand}
                aria-expanded={expanded}
                style={{
                    display: "block",
                    width: "100%",
                    padding: "11px 14px",
                    background: "none",
                    border: "none",
                    textAlign: "left",
                    cursor: canExpand ? "pointer" : "default",
                }}
            >
                <div style={{display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8}}>
                    <div style={{flex: 1, minWidth: 0}}>
                        <div style={{fontWeight: 600, fontSize: 14, color: isPast ? "#666" : "#1a1a1a"}}>
                            {match.homeTeam} vs. {match.guestTeam}
                        </div>
                        <div style={{fontSize: 12, color: "#777", marginTop: 3}}>
                            {formatDate(match.matchDateTime)}
                            {match.venue && ` · ${match.venue}`}
                        </div>
                        <div style={{fontSize: 11, color: "#aaa", marginTop: 2}}>
                            {match.league}{match.division ? ` – ${match.division}` : ""}
                        </div>
                        {!isPast && (
                            <AvailabilitySummary
                                availabilities={match.playerAvailabilities}
                                isMember={isMember}
                            />
                        )}
                    </div>
                    <div style={{textAlign: "right", flexShrink: 0}}>
                        {match.result && (
                            <div style={{
                                fontSize: 13,
                                fontWeight: 700,
                                color: "#1976d2",
                                fontVariantNumeric: "tabular-nums",
                            }}>
                                {match.result}
                            </div>
                        )}
                        {canExpand && (
                            <div style={{fontSize: 10, color: "#ccc", marginTop: 4}}>
                                {expanded ? "▲" : "▼"}
                            </div>
                        )}
                    </div>
                </div>
            </button>

            <div style={{padding: "0 14px 10px", display: "flex", flexDirection: "column", gap: 8}}>
                {currentUser && (
                    <AvailabilityButton
                        matchId={match.matchId}
                        teamId={teamId}
                        currentStatus={match.myAvailability}
                        currentComment={match.myComment}
                        isFuture={match.future}
                        isMember={isMember}
                        onChange={onAvailabilityChange}
                    />
                )}
                <div style={{display: "flex", gap: 8, flexWrap: "wrap"}}>
                    <ShareButton match={match} />
                    {match.matchUuid && match.future && (
                        <a
                            href={`/api/calendar/ical/tnb-match/${match.matchUuid}`}
                            download
                            title="Termin in Kalender übernehmen"
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                padding: "3px 10px",
                                background: "#e3f2fd",
                                color: "#1565c0",
                                borderRadius: 4,
                                textDecoration: "none",
                                fontSize: 12,
                                fontWeight: 600,
                            }}
                        >
                            + Kalender
                        </a>
                    )}
                    {match.tnbReportPath && (
                        <a
                            href={`https://tnb.liga.nu/cgi-bin/WebObjects/nuLigaTENDE.woa/wa/${match.tnbReportPath}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Spielbericht auf TNB"
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                padding: "3px 10px",
                                background: "#f3e5f5",
                                color: "#6a1b9a",
                                borderRadius: 4,
                                textDecoration: "none",
                                fontSize: 12,
                                fontWeight: 600,
                            }}
                        >
                            TNB ↗
                        </a>
                    )}
                </div>
            </div>

            {expanded && (
                <div style={{padding: "0 14px 12px", borderTop: "1px solid #f0f0f0"}}>
                    <PlayerAvailabilityList
                        availabilities={match.playerAvailabilities}
                        isMember={isMember}
                    />
                    {!isPast && canSeeLineup && (
                        <MatchLineupEditor
                            matchId={match.matchId}
                            teamId={teamId}
                            isCaptain={isCaptain}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

const SectionLabel = ({children}) => (
    <div style={{
        fontSize: 11,
        fontWeight: 600,
        color: "#999",
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        marginBottom: 8,
        marginTop: 4,
    }}>
        {children}
    </div>
);

const LeagueMatchList = ({teamId, matches, loading, currentUser, isMember, isCaptain, canSeeLineup, onAvailabilityChange, highlightMatchId}) => {
    const isPastHighlight = highlightMatchId != null && matches.some(m => m.matchId === highlightMatchId && !m.future);
    const [pastExpanded, setPastExpanded] = useState(isPastHighlight);

    useEffect(() => {
        if (!highlightMatchId || loading) return;
        // Falls das Spiel in den vergangenen Spielen liegt, kurz warten bis sie gerendert sind
        const timer = setTimeout(() => {
            const el = document.getElementById(`match-${highlightMatchId}`);
            if (el) el.scrollIntoView({behavior: "smooth", block: "center"});
        }, 100);
        return () => clearTimeout(timer);
    }, [highlightMatchId, loading, pastExpanded]); // eslint-disable-line

    if (loading) return <p style={{color: "#888"}}>Lade Spieltermine...</p>;
    if (matches.length === 0) {
        return (
            <p style={{color: "#666", fontStyle: "italic"}}>
                Keine Spieltermine für diese Mannschaft.
            </p>
        );
    }

    const futureMatches = matches.filter(m => m.future);
    const pastMatches = matches.filter(m => !m.future);

    const cardProps = {teamId, isMember, isCaptain, canSeeLineup, currentUser, onAvailabilityChange, highlightMatchId};

    return (
        <div>
            {/* Vergangene Spiele — oben, einklappbar */}
            {pastMatches.length > 0 && (
                <div style={{marginBottom: 16}}>
                    <button
                        onClick={() => setPastExpanded(v => !v)}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            width: "100%",
                            padding: "8px 12px",
                            background: "#f5f5f5",
                            border: "1px solid #e0e0e0",
                            borderRadius: 6,
                            cursor: "pointer",
                            fontSize: 13,
                            color: "#666",
                            textAlign: "left",
                            marginBottom: pastExpanded ? 10 : 0,
                        }}
                    >
                        <span style={{fontSize: 10, color: "#aaa"}}>{pastExpanded ? "▲" : "▼"}</span>
                        <span>{pastMatches.length} vergangene Spiele</span>
                    </button>
                    {pastExpanded && pastMatches.map(m => (
                        <MatchCard key={m.matchId} match={m} {...cardProps} />
                    ))}
                </div>
            )}

            {/* Bevorstehende Spiele */}
            {futureMatches.length > 0 ? (
                <div>
                    {pastMatches.length > 0 && <SectionLabel>Bevorstehende Spiele</SectionLabel>}
                    {futureMatches.map(m => (
                        <MatchCard key={m.matchId} match={m} {...cardProps} />
                    ))}
                </div>
            ) : (
                <p style={{color: "#777", fontStyle: "italic", fontSize: 14}}>
                    Keine bevorstehenden Spiele.
                </p>
            )}
        </div>
    );
};

export default LeagueMatchList;
